import apiV2 from '../api/v2';
import { apiErrorMessage } from '../utils/apiError';

// Complements the practiceDetail sparkAcct field once it holds a value - see
// SparkCredsAction. Distinct endpoint namespace (spark-creds) from
// practiceDetail.ts's spark-account-request email flow, so it lives in its
// own service file rather than growing that one further.

export interface SparkCredsLiveCheck {
    ok: boolean;
    reason?: string;
}

export interface SparkCredsStatus {
    sparkAcct: string | null;
    hasCreds: boolean;
    accessTokenExpiresAt: string | null;
    updatedAt: string | null;
    // Populated only when the caller requested `live=true` - a real call to
    // Spark using the stored token. null on a DB-only (`live=false`) fetch.
    live: SparkCredsLiveCheck | null;
}

export async function fetchSparkCredsStatus(
    practiceId: string,
    live: boolean
): Promise<SparkCredsStatus> {
    const response = await apiV2.get<{ success: boolean; data: SparkCredsStatus }>(
        `/practices/${practiceId}/spark-creds/status`,
        { params: { live } }
    );
    return response.data.data;
}

export type SparkCredsSyncAction = 'generated' | 'refreshed' | 'regenerated';

export interface SparkCredsSyncResult {
    practiceId: string;
    practiceName: string;
    action: SparkCredsSyncAction;
}

// First-time generation drives a headless browser upstream and can take up
// to ~90s on the backend - well above axios's default timeout. Must stay >=
// the backend's SYNC_TIMEOUT_MS (spark-creds.service.ts) or we abort first.
const SPARK_CREDS_SYNC_TIMEOUT_MS = 120_000;

export async function syncSparkCreds(
    practiceId: string,
    force: boolean
): Promise<SparkCredsSyncResult> {
    const response = await apiV2.post<{ success: boolean; data: SparkCredsSyncResult }>(
        `/practices/${practiceId}/spark-creds/sync`,
        { force },
        { timeout: SPARK_CREDS_SYNC_TIMEOUT_MS }
    );
    return response.data.data;
}

function axiosStatus(err: unknown): number | undefined {
    if (typeof err !== 'object' || err === null) return undefined;
    return (err as { response?: { status?: number } }).response?.status;
}

/**
 * Maps the sync endpoint's documented error statuses to a friendly fallback,
 * still preferring whatever message the backend actually sent (see
 * apiErrorMessage) - the fallback only kicks in when the response carries no
 * usable message of its own.
 */
export function sparkCredsSyncErrorMessage(err: unknown): string {
    switch (axiosStatus(err)) {
        case 422:
            return apiErrorMessage(
                err,
                'This practice has no Spark account set - add one before syncing credentials.'
            );
        case 503:
            return apiErrorMessage(err, 'Spark credential sync is not configured on the server.');
        case 404:
            return apiErrorMessage(err, 'This practice could not be found.');
        case 502:
            return apiErrorMessage(
                err,
                'Spark rejected the request. Try again, or use Force regenerate if it keeps failing.'
            );
        default:
            return apiErrorMessage(err, 'Failed to sync Spark credentials');
    }
}
